import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { Capacitor } from '@capacitor/core';
import { App as CapApp } from '@capacitor/app';
import type { Surface } from '@tupai/analytics';
import {
  captureFirstTouch,
  identifyParent,
  initAnalytics,
  resetIdentity,
} from '@tupai/analytics/client';
import './lib/language';
import { routeTree } from './routeTree.gen';
import NotFoundPage from './components/not-found-page';
import './index.css';
import { initBilling, identifyBillingUser } from './lib/billing';
import { initAppOpen } from './lib/app-open';
import {
  initPushNotifications,
  stopPushNotifications,
  type PushTapData,
} from './lib/push-notifications';
import { markNotificationReadById } from './lib/api';
import { resolveNotificationLink } from './lib/notification-link';
import { initRouterAnalytics } from './lib/analytics-router';
import { useAuthStore } from './store/auth-store';
import { useLinksStore } from './store/links-store';
import { useExamFilterStore } from './store/exam-filter-store';
import { useCoverageStore } from './store/coverage-store';
import * as Sentry from '@sentry/react';
import { LiveUpdate } from '@capawesome/capacitor-live-update';
import { updateApp } from './lib/ota-updater';

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_PARENT_CLIENT_DSN,
  dataCollection: {},
});

// Must run before the router exists: the `/` -> `/start` redirect drops the query string and its `utm_*` tags.
captureFirstTouch();

const router = createRouter({
  routeTree,
  defaultNotFoundComponent: NotFoundPage,
  // Unknown URLs render the bare not-found screen, never inside the _protected shell.
  notFoundMode: 'root',
  // Plain strings: the default JSON parser turns an invite code like 23456E78 into a number and rewrites the URL.
  parseSearch: (search) => Object.fromEntries(new URLSearchParams(search)),
  stringifySearch: (search) => {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(search)) {
      if (value != null) params.set(key, String(value));
    }
    const str = params.toString();
    return str ? `?${str}` : '';
  },
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
  interface HistoryState {
    trigger?: string;
    from?: string;
    seat?: string;
    openLinkSheet?: boolean;
    isFirstView?: boolean;
    childName?: string;
    generate?: boolean;
  }
}

initBilling()
  .then(() => {
    const { parentId } = useAuthStore.getState();
    if (parentId) return identifyBillingUser(parentId);
  })
  .catch((err) => console.warn('[billing] init failed:', err));

// Not awaited: a PostHog outage or missing key (normal in local dev) must not block startup; `.finally` so tracking wires up even if init rejects.
initAnalytics({
  app: 'parent',
  key: import.meta.env.VITE_POSTHOG_API_KEY,
  host: import.meta.env.POSTHOG_HOST,
  // 'web' | 'ios' | 'android' — Capacitor reports exactly these three.
  surface: Capacitor.getPlatform() as Surface,
  appVersion: import.meta.env.VITE_APP_VERSION,
})
  .catch((err) => console.warn('[analytics] init failed:', err))
  .finally(() => {
    initAppOpen();
    initRouterAnalytics(router);
  });

const { token, parentId: initialParentId } = useAuthStore.getState();
if (token) {
  useLinksStore.getState().refreshMyCode();
  useLinksStore.getState().refreshParentLinks();
}
// parentId is the analytics distinct_id; never use the phone number.
if (initialParentId) identifyParent(initialParentId);

useAuthStore.subscribe((state, prev) => {
  if (state.token && !prev.token) {
    useLinksStore.getState().refreshMyCode();
    useLinksStore.getState().refreshParentLinks();
    if (state.parentId) identifyParent(state.parentId);
  } else if (!state.token && prev.token) {
    useLinksStore.getState().reset();
    useExamFilterStore.getState().reset();
    useCoverageStore.getState().reset();
    resetIdentity();
    void router.invalidate();
  }
});

let pendingPushTap: PushTapData | null = null;
function openPushNotifications(): void {
  if (!pendingPushTap || !useAuthStore.getState().token || router.state.status !== 'idle') {
    return;
  }
  const { link, notification_id } = pendingPushTap;
  pendingPushTap = null;
  if (notification_id) {
    markNotificationReadById(notification_id).catch(console.error);
  }
  const to = resolveNotificationLink(router, link);
  if (to) router.history.push(to);
  else void router.navigate({ to: '/notifications' });
}
const unsubscribePushAuth = useAuthStore.subscribe(openPushNotifications);
const unsubscribePushRouter = router.subscribe('onResolved', openPushNotifications);
void initPushNotifications((data) => {
  pendingPushTap = data;
  openPushNotifications();
});
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    unsubscribePushAuth();
    unsubscribePushRouter();
    void stopPushNotifications();
  });
}

function App() {
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const initializeUpdater = async () => {
      const { id } = await CapApp.getInfo();
      // Wrong app's bundle: withholding ready() makes LiveUpdate roll back and block it.
      if (id !== 'ai.tupai.parent') return;

      await LiveUpdate.ready();

      const { versionCode } = await LiveUpdate.getVersionCode();
      console.info('[OTA] Native build:', {
        platform: Capacitor.getPlatform(),
        nativeBuild: versionCode,
      });

      await updateApp({ app: 'parent', mode: 'background' });
    };

    void initializeUpdater().catch((error) => {
      console.error('[OTA] Initialization failed:', error);
    });
  }, []);

  return <RouterProvider router={router} />;
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>
);
